import { Link } from 'react-router-dom';

const NEWSLETTER_URL = 'https://readdy.ai/api/form/d700716dp0k8rarn85t0';

export default function Footer() {
  const handleNewsletterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const params = new URLSearchParams();
    data.forEach((value, key) => params.append(key, value as string));

    fetch(NEWSLETTER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    }).then(() => {
      form.reset();
    });
  };

  return (
    <footer className="bg-[#FAF8F5] border-t border-gray-100">
      {/* Newsletter band */}
      <div className="bg-[#FF6B35] py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="text-white font-black text-2xl mb-1" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.05em' }}>
              Stay Updated
            </h3>
            <p className="text-white/80 text-sm">Get exclusive deals & new arrivals delivered to your inbox.</p>
          </div>
          <form
            data-readdy-form
            onSubmit={handleNewsletterSubmit}
            className="flex gap-2 w-full md:w-auto"
          >
            <input
              type="email"
              name="email"
              required
              placeholder="Your email address"
              className="flex-1 md:w-72 px-5 py-3 rounded-full text-sm bg-white/20 border border-white/30 text-white placeholder-white/60 outline-none focus:bg-white/30 transition-colors"
            />
            <button
              type="submit"
              className="bg-[#0D0D0D] text-white font-bold px-6 py-3 rounded-full hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              Subscribe
            </button>
          </form>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <Link to="/" className="inline-block mb-4 cursor-pointer">
              <img
                src="https://public.readdy.ai/ai/img_res/c5283671-54c4-4b1c-8551-32a9e5d65262.png"
                alt="SipDash Logo"
                className="h-10 w-auto object-contain"
              />
            </Link>
            <p className="text-gray-500 text-sm leading-relaxed mb-6 max-w-xs">
              Georgetown's #1 beverage delivery service. Cold drinks, fast delivery, every time — across the entire capital of Guyana.
            </p>
            <div className="flex items-start gap-1 text-sm text-gray-600 mb-2">
              <div className="w-4 h-4 flex items-center justify-center text-[#FF6B35] flex-shrink-0 mt-0.5">
                <i className="ri-map-pin-line" />
              </div>
              <div>
                <span className="block">Lot 2555 Providence</span>
                <span className="block">East Bank, Georgetown, Guyana</span>
              </div>
            </div>
            <div className="flex items-center gap-1 text-sm text-gray-600">
              <div className="w-4 h-4 flex items-center justify-center text-[#FF6B35]">
                <i className="ri-time-line" />
              </div>
              <span>Delivery: 10:00 AM – 12:00 PM</span>
            </div>
            <div className="flex gap-3 mt-6">
              {['ri-instagram-line', 'ri-facebook-circle-line', 'ri-twitter-x-line', 'ri-whatsapp-line'].map((icon) => (
                <a
                  key={icon}
                  href="/"
                  className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-full text-gray-500 hover:bg-[#FF6B35] hover:text-white transition-colors cursor-pointer"
                >
                  <i className={`${icon} text-base`} />
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          {[
            {
              title: 'Company',
              links: ['About Us', 'Careers', 'Press', 'Blog', 'Partners'],
            },
            {
              title: 'Support',
              links: ['Help Center', 'Safety', 'Terms of Service', 'Privacy Policy', 'Refunds'],
            },
            {
              title: 'Join Us',
              links: ['Become a Partner', 'Delivery Jobs', 'Business Orders', 'Affiliate Program'],
            },
          ].map((col) => (
            <div key={col.title}>
              <h4 className="font-bold text-gray-900 text-sm mb-4 uppercase tracking-wider">{col.title}</h4>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link}>
                    <a href="/" rel="nofollow" className="text-gray-500 text-sm hover:text-[#FF6B35] transition-colors cursor-pointer">
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Find Us — Map section */}
        <div className="mt-14 pt-10 border-t border-gray-100">
          <h4 className="font-bold text-gray-900 text-sm mb-6 uppercase tracking-wider flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center text-[#FF6B35]">
              <i className="ri-map-pin-2-line text-base" />
            </div>
            Find Us
          </h4>
          <div className="grid md:grid-cols-3 gap-6 items-start">
            {/* Map embed */}
            <div className="md:col-span-2 rounded-2xl overflow-hidden border border-gray-100" style={{ height: '280px' }}>
              <iframe
                title="SipDash Location"
                src="https://www.google.com/maps?q=Providence,+East+Bank+Demerara,+Guyana&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {/* Address details */}
            <div className="flex flex-col gap-5">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Address</p>
                <p className="text-sm font-semibold text-gray-900 leading-relaxed">
                  Lot 2555 Providence<br />
                  East Bank Demerara<br />
                  Georgetown, Guyana
                </p>
              </div>

              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Delivery Hours</p>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Mon – Fri</span>
                    <span className="font-medium text-gray-800">10:00 AM – 12:00 PM</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Sat – Sun</span>
                    <span className="font-medium text-gray-800">10:00 AM – 12:00 PM</span>
                  </div>
                </div>
              </div>

              <a
                href="https://www.google.com/maps?q=Providence,+East+Bank+Demerara,+Guyana"
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#e85d2a] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap w-fit"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-2-line text-sm" />
                </div>
                Open in Maps
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-100 mt-12 pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-gray-400 text-xs">
            © 2026 SipDash Georgetown. All rights reserved. Serving Georgetown, Guyana.
          </p>
          <div className="flex items-center gap-4">
            <span className="text-gray-400 text-xs">Available on</span>
            <div className="flex gap-2">
              <div className="flex items-center gap-1.5 bg-gray-100 rounded-lg px-3 py-1.5 cursor-pointer hover:bg-gray-200 transition-colors">
                <div className="w-4 h-4 flex items-center justify-center text-gray-700">
                  <i className="ri-apple-line text-sm" />
                </div>
                <span className="text-xs font-semibold text-gray-700">App Store</span>
              </div>
              <div className="flex items-center gap-1.5 bg-gray-100 rounded-lg px-3 py-1.5 cursor-pointer hover:bg-gray-200 transition-colors">
                <div className="w-4 h-4 flex items-center justify-center text-gray-700">
                  <i className="ri-google-play-line text-sm" />
                </div>
                <span className="text-xs font-semibold text-gray-700">Google Play</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
